import { useCallback, useEffect, useState } from "react";
import { Check, Copy, Loader2, Megaphone, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { API, api, errorDetail } from "@/lib/api";

// The winners' post MiLu drafts the morning the board settles — copy it, or let her send it.
export const Announcements = () => {
  const [drafts, setDrafts] = useState(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(() => {
    api.get("/admin/announce")
      .then(({ data }) => setDrafts(data.drafts || []))
      .catch((e) => console.error("could not load the drafted posts", e));
  }, []);

  useEffect(load, [load]);

  const act = async (what, run) => {
    setBusy(what);
    try {
      await run();
      load();
    } catch (e) {
      toast.error(errorDetail(e) || "That did not work");
    } finally {
      setBusy("");
    }
  };

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("The post is copied — paste it wherever you like");
    } catch {
      toast.error("Your browser would not let me copy that");
    }
  };

  return (
    <div className="rounded-3xl border border-white/10 bg-panel p-6" data-testid="announcements">
      <div className="flex flex-wrap items-center gap-3">
        <p className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-widest text-ink-soft">
          <Megaphone className="h-4 w-4 text-brand" /> Winners' announcement
        </p>
        <button
          data-testid="announce-build"
          onClick={() => act("build", () => api.post("/admin/announce/build"))}
          disabled={busy === "build"}
          className="ms-auto inline-flex min-h-[32px] items-center gap-2 rounded-full border border-white/15 px-3 text-[11px] text-ink-soft transition-colors hover:border-brand/50 hover:text-brand disabled:opacity-50"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${busy === "build" ? "animate-spin" : ""}`} />
          Draft last month's
        </button>
      </div>

      {!drafts ? (
        <Loader2 className="mt-4 h-4 w-4 animate-spin text-ink-soft" />
      ) : drafts.length === 0 ? (
        <p className="mt-4 text-[11px] text-ink-soft" data-testid="announce-empty">
          Nothing drafted yet — MiLu writes one the morning a board settles.
        </p>
      ) : (
        <ul className="mt-4 space-y-4">
          {drafts.map((one) => (
            <li key={one.id} data-testid={`announce-${one.month}`}
                className="rounded-2xl border border-white/10 bg-page p-4">
              <p className="flex flex-wrap items-center gap-2 text-xs">
                <b>{one.month}</b>
                <span className="text-ink-soft">{one.winners?.length || 0} winners</span>
                {one.posted_at ? (
                  <span className="inline-flex items-center gap-1 rounded-full border border-forest/40 bg-forest/10 px-2 py-0.5 text-[10px] text-forest"
                        data-testid={`announce-posted-${one.month}`}>
                    <Check className="h-3 w-3" /> posted {one.posted_where}
                  </span>
                ) : (
                  <span className="rounded-full border border-[#F5C451]/40 bg-[#F5C451]/10 px-2 py-0.5 text-[10px] text-[#F5C451]">
                    waiting to go out
                  </span>
                )}
              </p>

              <pre className="mt-3 whitespace-pre-wrap font-sans text-[11px] leading-relaxed text-ink-soft"
                   data-testid={`announce-text-${one.month}`}>{one.text}</pre>

              {one.cards?.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {one.cards.map((src, i) => (
                    <a key={src} href={`${API.replace(/\/api$/, "")}${src}`} target="_blank" rel="noreferrer"
                       data-testid={`announce-card-${one.month}-${i + 1}`}>
                      <img src={`${API.replace(/\/api$/, "")}${src}`} alt={`winner ${i + 1}`} loading="lazy"
                           onError={(e) => { e.currentTarget.style.display = "none"; }}
                           className="h-16 w-auto rounded-lg border border-white/10" />
                    </a>
                  ))}
                </div>
              )}

              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  data-testid={`announce-copy-${one.month}`}
                  onClick={() => copy(one.text)}
                  className="inline-flex min-h-[32px] items-center gap-2 rounded-full border border-white/15 px-3 text-[11px] text-ink-soft transition-colors hover:border-brand/50 hover:text-brand"
                >
                  <Copy className="h-3.5 w-3.5" /> Copy the post
                </button>
                {!one.posted_at && (
                  <button
                    data-testid={`announce-done-${one.month}`}
                    onClick={() => act(`done-${one.id}`,
                      () => api.post(`/admin/announce/${one.id}/posted`))}
                    className="min-h-[32px] rounded-full border border-white/15 px-3 text-[11px] text-ink-soft transition-colors hover:border-brand/50 hover:text-brand"
                  >
                    I posted it myself
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
